import apiV2 from '../../api/v2';
import type { TiptapDoc } from '@odo/shared-ui';

export type NoteSeverity = 'info' | 'warning' | 'critical';
export type NoteVisibility = 'internal' | 'external';

export interface PatientNote {
    id: string;
    patientId: string;
    severity: NoteSeverity;
    visibility: NoteVisibility;
    title: string;
    body: string;
    /** Canonical rich-text content. Null on legacy notes created before the
     *  rich-editor migration — render `body` (plain text) as a fallback. */
    bodyDoc: TiptapDoc | null;
    pinned: boolean;
    authorId: string;
    authorName: string;
    authorInitials: string;
    createdAt: string;
    updatedAt: string;
}

export interface CreateNoteInput {
    title: string;
    bodyDoc: TiptapDoc;
}

export interface UpdateNoteInput {
    title?: string;
    bodyDoc?: TiptapDoc;
    pinned?: boolean;
}

/**
 * Lists patient notes visible to a doctor. Always requests external-only
 * visibility — the server hard-filters this for non-admin callers too, but
 * an admin viewing the doctor dashboard must also see external-only notes.
 */
export async function fetchNotes(patientId: string): Promise<PatientNote[]> {
    const response = await apiV2.get<PatientNote[]>(
        `/patients/${encodeURIComponent(patientId)}/notes`,
        { params: { visibility: 'external' } }
    );
    return response.data ?? [];
}

/**
 * Creates a note. Both `severity` and `visibility` are sent explicitly:
 *
 * - `severity` is a Zod-required field on the create schema (no server
 *   default), so a value must be sent even though there is no picker here.
 * - `visibility` MUST be sent because the server only coerces it to
 *   `'external'` for non-admin callers. An admin creating a note from the
 *   doctor dashboard would otherwise fall through to the schema default of
 *   `'internal'`, and the note would vanish from this app's own list — which
 *   always requests `?visibility=external`.
 *
 * Non-admin callers have both values overwritten server-side with these exact
 * values, so sending them is a no-op for doctors and a correction for admins.
 */
export async function createNote(patientId: string, input: CreateNoteInput): Promise<PatientNote> {
    const response = await apiV2.post<PatientNote>(
        `/patients/${encodeURIComponent(patientId)}/notes`,
        {
            ...input,
            severity: 'info' satisfies NoteSeverity,
            visibility: 'external' satisfies NoteVisibility
        }
    );
    return response.data;
}

/**
 * Updates a note. Never sends `severity`/`visibility` — the server rejects
 * updates containing either field with a 400.
 */
export async function updateNote(
    patientId: string,
    noteId: string,
    input: UpdateNoteInput
): Promise<PatientNote> {
    const response = await apiV2.patch<PatientNote>(
        `/patients/${encodeURIComponent(patientId)}/notes/${encodeURIComponent(noteId)}`,
        input
    );
    return response.data;
}

export async function deleteNote(patientId: string, noteId: string): Promise<void> {
    await apiV2.delete(
        `/patients/${encodeURIComponent(patientId)}/notes/${encodeURIComponent(noteId)}`
    );
}
